/* eslint-disable statura/no-floating-point-money --
 * This module computes care-MINUTES (time), not money. The lint rule
 * fires on variable names containing 'Target' / 'Total'; since these
 * are minutes (integer-friendly), float drift is irrelevant.
 */

/**
 * Phase 4 §4.8 — Care-minutes pre-publish calculator.
 *
 * Department care-minute guidance frames 215 total minutes and 44 RN
 * minutes per resident day as the current sector-wide average baseline.
 * Individual residential-home targets can differ and are calculated under
 * Aged Care Rules 2025 s 176-20; callers should pass the configured
 * `care_minute_targets` values whenever available.
 *
 * The pure aggregator below maps a roster period's shifts (residential
 * facility only) to the care-minute target. The output feeds the
 * `CARE_MINUTES_SHORTFALL` gate (currently deferred in
 * `roster-gate-detection.ts`).
 *
 * Targets are configurable per tenant and facility. Defaults are fallback
 * baselines only; they are not proof that a specific home has met its
 * current Department-calculated target.
 *
 * NB: residency = number of residents on a given day. The caller must
 * supply this (varies daily; weekend/holiday discharge patterns matter).
 */

export interface CareMinutesShift {
  /** YYYY-MM-DD in tenant TZ. */
  date: string;
  /** Effective minutes of CARE delivered (typically paidMinutes minus
   *  break + travel + admin overhead). Caller-computed; the calculator
   *  doesn't second-guess what counts as "care". */
  careMinutes: number;
  /** Whether this worker is an RN (counts toward the 44-min RN target). */
  isRn: boolean;
  /** Optional: which facility this shift belongs to. Used for per-facility
   *  aggregation; can be omitted for tenant-wide aggregation. */
  facilityId?: string | null;
}

export interface CareMinutesInput {
  shifts: CareMinutesShift[];
  /** Number of residents on each date in the period. Map keyed by
   *  YYYY-MM-DD. Days not in the map default to the input's
   *  `defaultResidentCount` (or 0 if neither is supplied). */
  residentsByDate?: Record<string, number>;
  defaultResidentCount?: number;
  /** Total target — default 215 min per resident per day. */
  totalTargetMinutes?: number;
  /** RN target — default 44 min per resident per day. */
  rnTargetMinutes?: number;
  /** Per-facility scope: if set, only shifts with matching facility_id
   *  contribute, and residentsByDate represents that facility. */
  facilityId?: string | null;
}

export interface CareMinutesResult {
  /** Per-day actuals + shortfall. */
  byDate: Array<{
    date: string;
    residentCount: number;
    targetTotalMinutes: number;
    actualTotalMinutes: number;
    targetRnMinutes: number;
    actualRnMinutes: number;
    totalShortfall: number;
    rnShortfall: number;
    /** Operational indicator only; one day's shortfall is not a Rules breach. */
    operationalShortfall: boolean;
    /** @deprecated Use operationalShortfall. Retained for existing roster consumers. */
    breach: boolean;
  }>;
  /** Period summary. */
  periodTotalShortfall: number;
  periodRnShortfall: number;
  /** Number of days with an operational shortfall. */
  operationalShortfallDays: number;
  /** @deprecated Use operationalShortfallDays; this is not a statutory breach count. */
  breachDays: number;
  /** Total residents-days observed across the period. */
  residentDays: number;
}

const DEFAULT_TOTAL_TARGET = 215;
const DEFAULT_RN_TARGET = 44;

export const CARE_MINUTE_ROLE_OPTIONS = [
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'PERSONAL_CARE_WORKER',
  'ASSISTANT_IN_NURSING',
  'AGENCY_CONTRACTOR',
  'CLINICAL_LEAD',
  'ALLIED_HEALTH',
] as const;

export type CareMinuteRoleOption = (typeof CARE_MINUTE_ROLE_OPTIONS)[number];
export type CareMinuteRoleBucket = 'RN' | 'EN' | 'PCW' | 'OTHER';

export const CARE_MINUTE_RN_ROLES = ['REGISTERED_NURSE'] as const;
export const CARE_MINUTE_EN_ROLES = ['ENROLLED_NURSE'] as const;
export const CARE_MINUTE_PCW_ROLES = [
  'CARE_WORKER',
  'PERSONAL_CARE_WORKER',
  'ASSISTANT_IN_NURSING',
  'AGENCY_CONTRACTOR',
] as const;

const CARE_MINUTE_RN_ROLE_SET = new Set<string>(CARE_MINUTE_RN_ROLES);
const CARE_MINUTE_EN_ROLE_SET = new Set<string>(CARE_MINUTE_EN_ROLES);
const CARE_MINUTE_PCW_ROLE_SET = new Set<string>(CARE_MINUTE_PCW_ROLES);
const CARE_MINUTE_ROLE_OPTION_SET = new Set<string>(CARE_MINUTE_ROLE_OPTIONS);

export function careMinuteRoleSelectOptions(currentRole?: string | null): string[] {
  const normalisedRole = String(currentRole ?? '')
    .trim()
    .toUpperCase();
  return normalisedRole && !CARE_MINUTE_ROLE_OPTION_SET.has(normalisedRole)
    ? [normalisedRole, ...CARE_MINUTE_ROLE_OPTIONS]
    : [...CARE_MINUTE_ROLE_OPTIONS];
}

export function classifyCareMinuteRoleBucket(
  role: string | null | undefined,
): CareMinuteRoleBucket {
  const normalisedRole = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (CARE_MINUTE_RN_ROLE_SET.has(normalisedRole)) return 'RN';
  if (CARE_MINUTE_EN_ROLE_SET.has(normalisedRole)) return 'EN';
  if (CARE_MINUTE_PCW_ROLE_SET.has(normalisedRole)) return 'PCW';
  if (
    normalisedRole === 'RN' ||
    normalisedRole === 'LRN' ||
    normalisedRole === 'RNAGY' ||
    normalisedRole === 'REGISTERED_NURSE' ||
    normalisedRole.startsWith('RN_') ||
    normalisedRole.startsWith('LRN_') ||
    normalisedRole.startsWith('RNAGY_') ||
    normalisedRole.startsWith('LEAD_RN')
  ) {
    return 'RN';
  }
  if (
    normalisedRole === 'EN' ||
    normalisedRole === 'ENROLLED_NURSE' ||
    normalisedRole.startsWith('EN_') ||
    normalisedRole.startsWith('ENROLLED_NURSE')
  ) {
    return 'EN';
  }
  if (
    normalisedRole === 'PCA' ||
    normalisedRole === 'PCW' ||
    normalisedRole === 'AIN' ||
    normalisedRole === 'HCA' ||
    normalisedRole === 'PERSONAL_CARE' ||
    normalisedRole === 'PERSONAL_CARE_WORKER' ||
    normalisedRole === 'ASSISTANT_IN_NURSING' ||
    normalisedRole.startsWith('GS_PCA') ||
    normalisedRole.startsWith('PCA_') ||
    normalisedRole.startsWith('PCW_') ||
    normalisedRole.startsWith('AIN_') ||
    normalisedRole.startsWith('HCA_')
  ) {
    return 'PCW';
  }
  return 'OTHER';
}

export function calculateCareMinutesAchievement(input: CareMinutesInput): CareMinutesResult {
  const totalTarget = input.totalTargetMinutes ?? DEFAULT_TOTAL_TARGET;
  const rnTarget = input.rnTargetMinutes ?? DEFAULT_RN_TARGET;
  const defaultResidents = input.defaultResidentCount ?? 0;

  // Filter by facility if requested
  const relevantShifts = input.facilityId
    ? input.shifts.filter((s) => s.facilityId === input.facilityId)
    : input.shifts;

  // Aggregate by date
  const byDate = new Map<string, { total: number; rn: number }>();
  for (const s of relevantShifts) {
    const cur = byDate.get(s.date) ?? { total: 0, rn: 0 };
    cur.total += s.careMinutes;
    if (s.isRn) cur.rn += s.careMinutes;
    byDate.set(s.date, cur);
  }

  // Build per-day results
  const dates = Array.from(byDate.keys()).sort();
  let periodTotalShortfall = 0;
  let periodRnShortfall = 0;
  let breachDays = 0;
  let residentDays = 0;

  const results = dates.map((d) => {
    const a = byDate.get(d)!;
    const residents = input.residentsByDate?.[d] ?? defaultResidents;
    const targetTotal = totalTarget * residents;
    const targetRn = rnTarget * residents;
    const totalShortfall = Math.max(0, targetTotal - a.total);
    const rnShortfall = Math.max(0, targetRn - a.rn);
    const operationalShortfall = totalShortfall > 0 || rnShortfall > 0;
    if (operationalShortfall) breachDays++;
    periodTotalShortfall += totalShortfall;
    periodRnShortfall += rnShortfall;
    residentDays += residents;
    return {
      date: d,
      residentCount: residents,
      targetTotalMinutes: targetTotal,
      actualTotalMinutes: a.total,
      targetRnMinutes: targetRn,
      actualRnMinutes: a.rn,
      totalShortfall,
      rnShortfall,
      operationalShortfall,
      breach: operationalShortfall,
    };
  });

  return {
    byDate: results,
    periodTotalShortfall,
    periodRnShortfall,
    breachDays,
    operationalShortfallDays: breachDays,
    residentDays,
  };
}

/** One complete local reporting day at one residential home. */
export interface QuarterlyCareMinutesDay {
  date: string;
  residentDays: number;
  /** Hospital leave from day 29 onward is excluded under r 176-15(5)(b). */
  hospitalLeaveFromDay29: number;
  rnMinutes: number;
  enMinutes: number;
  otherCareMinutes: number;
  /** Home-specific daily targets under r 176-20; never infer legal targets from defaults. */
  rnTargetPerResident: number;
  combinedTargetPerResident: number;
}

export interface QuarterlyCareMinutesResult {
  eligibleResidentDays: number;
  rnMinutes: number;
  rnAndEnMinutes: number;
  combinedMinutes: number;
  rnTargetMinutes: number;
  combinedTargetMinutes: number;
  /** r 176-15(2): combined care at least 100% of the quarterly target. */
  combinedMet: boolean | null;
  /** r 176-15(3): RN care at least 90% of the quarterly RN target. */
  rnNinetyPercentMet: boolean | null;
  /** r 176-15(4): RN plus EN care at least 100% of the RN target. */
  rnAndEnMet: boolean | null;
}

/**
 * Evaluates the quarter as a whole. Daily red cells remain useful staffing
 * signals, but the Rules compare quarter totals with quarter targets.
 * Null results mean there are no eligible resident days to assess.
 */
export function evaluateQuarterlyCareMinutes(
  days: QuarterlyCareMinutesDay[],
): QuarterlyCareMinutesResult {
  if (
    days.some(
      (day) =>
        [
          day.residentDays,
          day.hospitalLeaveFromDay29,
          day.rnMinutes,
          day.enMinutes,
          day.otherCareMinutes,
          day.rnTargetPerResident,
          day.combinedTargetPerResident,
        ].some((value) => !Number.isFinite(value) || value < 0) ||
        day.hospitalLeaveFromDay29 > day.residentDays,
    )
  ) {
    throw new Error('Care-minute input must contain non-negative values and valid resident days.');
  }
  const result = days.reduce<QuarterlyCareMinutesResult>(
    (sum, day) => {
      const eligible = day.residentDays - day.hospitalLeaveFromDay29;
      sum.eligibleResidentDays += eligible;
      sum.rnMinutes += day.rnMinutes;
      sum.rnAndEnMinutes += day.rnMinutes + day.enMinutes;
      sum.combinedMinutes += day.rnMinutes + day.enMinutes + day.otherCareMinutes;
      sum.rnTargetMinutes += eligible * day.rnTargetPerResident;
      sum.combinedTargetMinutes += eligible * day.combinedTargetPerResident;
      return sum;
    },
    {
      eligibleResidentDays: 0,
      rnMinutes: 0,
      rnAndEnMinutes: 0,
      combinedMinutes: 0,
      rnTargetMinutes: 0,
      combinedTargetMinutes: 0,
      combinedMet: null,
      rnNinetyPercentMet: null,
      rnAndEnMet: null,
    },
  );
  if (result.eligibleResidentDays > 0) {
    result.combinedMet = result.combinedMinutes >= result.combinedTargetMinutes;
    result.rnNinetyPercentMet = result.rnMinutes >= result.rnTargetMinutes * 0.9;
    result.rnAndEnMet = result.rnAndEnMinutes >= result.rnTargetMinutes;
  }
  return result;
}
